// src/designTokens.js
//
// Single source of truth for the console's visual language.
//
// Before this existed there were three competing identities: a dead theme.js
// (navy #1A237E + amber + Inter, never imported), the live inline theme in
// index.js (charcoal + muted gold + Oswald), and the components themselves,
// which hardcoded 58 distinct hex values — mostly Tailwind's default gray
// ramp plus MUI's default blue #1976d2. The result read as a generic admin
// template rather than LIVLIFE.
//
// Palette rationale: the console is an operations tool — dense, scanned, not
// read. So the neutrals carry the interface and the brand gold is reserved
// for genuine emphasis (active nav, focus, key figures). Neutrals are warmed
// very slightly toward the gold rather than pure Tailwind gray, so the two
// families look chosen together instead of merely adjacent.
//
// Status colors are deliberately separate from the brand accent: "this row
// needs attention" must never be confused with "this is a LIVLIFE thing."

export const brand = {
  // Matches LLTheme.Colors.gold in the iOS app — one gold across the product.
  gold: '#C59A45',
  goldDeep: '#9C7A34',
  goldWash: 'rgba(197, 154, 69, 0.10)',
};

export const ink = {
  900: '#17140F', // headings, primary figures
  700: '#33302A', // body text
  500: '#6B6459', // secondary text, column headers
  300: '#A9A296', // disabled, placeholder
};

export const surface = {
  page: '#FAF8F5',   // app background — warm, not Tailwind's cool #f9fafb
  card: '#FFFFFF',
  sunken: '#F2EEE7', // table header, hover fill
  line: '#E6E0D6',   // borders and dividers
};

// Semantic status — used for state, never for decoration.
export const status = {
  success: '#3F7A55',
  successBg: '#E6F0E9',
  warning: '#A8761C',
  warningBg: '#FBF1DE',
  danger: '#A33B32',
  dangerBg: '#F8E7E5',
  info: '#3C5A78',
  infoBg: '#E7EDF3',
  neutral: '#6B6459',
  neutralBg: '#F0ECE5',
};

// Type scale — one modular scale, so sizes stop being invented per component.
// Depth — the interactive half.
//
// The reference was a subject floating in light with real dimension. On a
// light ground that cannot come from glow (which only reads on dark), so it
// comes from shadow and lift instead: a soft warm cast shadow that deepens on
// hover, a gold rim on the focused element, and a faint warm wash behind a
// hero figure. Reserved for cards and hero surfaces — never table rows, where
// shadow behind every line would wreck scanning.
export const depth = {
  // Warm, not neutral-grey: a grey shadow under a gold-accented card reads
  // muddy, a warm one reads like light falling through the same room.
  lift: '0 2px 4px -2px rgba(90, 74, 44, 0.10), 0 12px 28px -18px rgba(90, 74, 44, 0.28)',
  liftHover: '0 4px 8px -3px rgba(90, 74, 44, 0.14), 0 22px 44px -20px rgba(90, 74, 44, 0.40)',
  ring: '0 0 0 1px rgba(197, 154, 69, 0.55)',
  // A whisper of warm light behind a hero figure — the light-mode equivalent
  // of the spotlight, dialled far down so it never fights the text.
  spotlight: 'radial-gradient(ellipse 75% 60% at 50% 0%, rgba(197, 154, 69, 0.07), transparent 72%)',
  glowText: 'none',
};

export const type = {
  display: 28,
  h1: 22,
  h2: 18,
  h3: 15,
  body: 14,
  small: 12.5,
  micro: 11,
};

export const radius = { sm: 4, md: 6, lg: 10 };

// Dashboards are scanned, so motion stays subtle and short.
export const motion = {
  fast: '140ms cubic-bezier(0.2, 0, 0, 1)',
  base: '220ms cubic-bezier(0.2, 0, 0, 1)',
};

/// Chip/pill colors for a status string, so every screen renders the same
/// status the same way instead of each page picking its own greens and reds.
export function statusTone(value) {
  const v = String(value || '').toLowerCase();
  if (['completed', 'succeeded', 'approved', 'paid', 'signed', 'active', 'eligible', 'verified'].includes(v)) {
    return { fg: status.success, bg: status.successBg };
  }
  if (['pending', 'processing', 'requested', 'sent', 'requires_capture', 'available', 'paused'].includes(v)) {
    return { fg: status.warning, bg: status.warningBg };
  }
  if (['failed', 'rejected', 'declined', 'canceled', 'cancelled', 'blocked', 'void', 'error'].includes(v)) {
    return { fg: status.danger, bg: status.dangerBg };
  }
  if (['confirmed', 'viewed', 'draft'].includes(v)) {
    return { fg: status.info, bg: status.infoBg };
  }
  return { fg: status.neutral, bg: status.neutralBg };
}

const tokens = { brand, ink, surface, status, depth, type, radius, motion, statusTone };
export default tokens;
